<script setup lang="ts">
import type { SectionStatuses } from '~/store-v2/financings/useFinancing'
import type { InvoiceFormOptions } from '~/ui-blocks/Formalization/blocks/paymentModel/Invoice/Invoice.types'
import type { PaymentModelAlert, ReceiptModelSectionKey } from '~/ui-blocks/Formalization/blocks/paymentModel/paymentModel.types'
import type { StageFormalization } from '~/ui-blocks/Formalization/Formalization.types'
import type { EquipmentOrigin, PostPaymentModelBody } from '~/utils/receipt-model/ReceiptModel'
import { useToast } from '@solfacil/girassol'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import IconLock from '~icons/material-symbols/lock'
import { InvoiceKind, InvoiceStatus, InvoiceType, PaymentModelStatus } from '~/constants/receiptModel'
import queryClient, { queryKeys } from '~/queryClient'
import hotjar from '~/services-v2/hotjar/hotjar'
import usePaymentModel from '~/store-v2/payment-model/usePaymentModel'
import useSuppliers from '~/store-v2/payment-model/useSuppliers'
import { useUpdatePaymentModel } from '~/store-v2/payment-model/useUpdatePaymentModel'
import { Formalization } from '~/types/enum'
import ConfirmationModal from '~/ui-blocks/Formalization/blocks/paymentModel/ConfirmationModal.vue'
import EquipmentSummary from '~/ui-blocks/Formalization/blocks/paymentModel/equipmentSummary/EquipmentSummary.vue'
import CommissionInvoiceInfo from '~/ui-blocks/Formalization/blocks/paymentModel/Invoice/CommissionInvoiceInfo.vue'
import InvoicesForm from '~/ui-blocks/Formalization/blocks/paymentModel/Invoice/Invoice.form.vue'
import * as InvoiceSchema from '~/ui-blocks/Formalization/blocks/paymentModel/Invoice/Invoice.schema'
import { SUPPLIER_SECTION_ID } from '~/ui-blocks/Formalization/blocks/paymentModel/paymentModel.constants'
import PaymentModelFooter from '~/ui-blocks/Formalization/blocks/paymentModel/PaymentModelFooter.vue'
import PaymentModelHeader from '~/ui-blocks/Formalization/blocks/paymentModel/PaymentModelHeader.vue'
import PaymentModelLoadError from '~/ui-blocks/Formalization/blocks/paymentModel/PaymentModelLoadError.vue'
import ReceiptModelSection from '~/ui-blocks/Formalization/blocks/paymentModel/receiptModelSelect/ReceiptModelSection.vue'
import { useReceiptModelOptions } from '~/ui-blocks/Formalization/blocks/paymentModel/receiptModelSelect/useReceiptModelOptions'
import ReceivableValues from '~/ui-blocks/Formalization/blocks/paymentModel/receivables/ReceivableValues.vue'
import SupplierInformationForm from '~/ui-blocks/Formalization/blocks/paymentModel/supplierInformation/SupplierInformation.form.vue'
import * as SupplierSchema from '~/ui-blocks/Formalization/blocks/paymentModel/supplierInformation/SupplierInformation.schema'
import { usePaymentModelAccess } from '~/ui-blocks/Formalization/blocks/paymentModel/usePaymentModelAccess'
import * as receiptModelFlow from '~/utils/receipt-model/receiptModelFlow'
import {
  formatSupplierName,
  getSelectedSupplierId,
  getSelectedSupplierName,
  resolveSupplierIsPartner,
} from '~/utils/receipt-model/supplier'

const props = defineProps<{
  position: number
  stage: StageFormalization
  stageReproved: string
  statusProject: SectionStatuses
  isOnflexPartner: boolean
}>()

const STORE_ORDER_EXPIRED_REASON_CODE = 'STORE_ORDER_EXPIRED'
const DEADLINE_ALERT_HIDDEN_STATUSES: ReadonlySet<string> = new Set<string>([
  PaymentModelStatus.UNDER_ANALYSIS,
  PaymentModelStatus.APPROVED,
  PaymentModelStatus.TO_REVIEW,
])

const { t } = useI18n()
const { createErrorToast, createSuccessToast } = useToast()
const route = useRoute()
const projectId = computed(() => ('id' in route.params ? String(route.params.id) : ''))

const access = usePaymentModelAccess({
  projectId,
  statusProject: () => props.statusProject,
  stage: () => props.stage,
  stageReproved: () => props.stageReproved,
})
const {
  financing,
  stepStatus,
  isFieldLocked,
  isSendDisabled,
  isTerminalReadOnly,
  hasShownStatus,
  showDateProps,
  dateProps,
  submittedAt,
  completedAt,
  reprovedAt,
  isBlocked,
  openCollapse,
  tag,
  showPendingEquipmentTag,
} = access

const {
  data: paymentModelData,
  isLoading: isLoadingPaymentModel,
  error: errorPaymentModel,
  refetch: refetchPaymentModel,
} = usePaymentModel(projectId, access.enablePooling)
const { data: suppliersData } = useSuppliers()
const { mutateAsync: mutateUpdatePaymentModel } = useUpdatePaymentModel(projectId)

const { payoutOptions, selectedPayoutOption, selectPayoutOption, receiptModel } = useReceiptModelOptions(paymentModelData)

// Terminal financing: the receipt model selection is read-only (handler guard, besides `disabled`).
function handlePayoutOptionSelect(code: string) {
  if (isTerminalReadOnly.value)
    return

  selectPayoutOption(code)
}

const equipmentOrigin = computed<EquipmentOrigin>(() => paymentModelData.value?.equipment_origin ?? null)
const hasCombo = computed(() => Boolean(paymentModelData.value?.has_combo))
const isContainer = computed(() => equipmentOrigin.value === 'container')
const isMirrorOrigin = computed(() => equipmentOrigin.value === 'mirror')
const hasComboOrMirror = computed(() => hasCombo.value || isMirrorOrigin.value)
const isComboStoreOrder = computed(() => receiptModelFlow.isComboStoreOrder({
  equipmentOrigin: equipmentOrigin.value,
  hasCombo: hasCombo.value,
}))
const isNonComboFlow = computed(() => !hasCombo.value && !isMirrorOrigin.value && !isContainer.value)

const appliesInvoiceKind = computed(() =>
  !hasCombo.value && !isMirrorOrigin.value && receiptModel.value !== InvoiceType.SERVICE_INVOICE,
)
const isSingleInvoice = computed(() => appliesInvoiceKind.value && receiptModel.value === InvoiceType.SINGLE_INVOICE)
const isSeparateInvoices = computed(() => appliesInvoiceKind.value && receiptModel.value === InvoiceType.SEPARATE_INVOICES)
const hasFinancingCommissionToReceive = computed(() => (paymentModelData.value?.commissions?.financing?.value ?? 0) > 0)

const showSupplierSection = computed(() =>
  receiptModelFlow.requiresSupplierSection(equipmentOrigin.value, receiptModel.value),
)
const invoicesToShow = computed(() =>
  (!hasCombo.value && !isMirrorOrigin.value) || hasFinancingCommissionToReceive.value || isContainer.value,
)
const invoiceFormOptions = computed<InvoiceFormOptions>(() => ({
  hasFinancingCommissionToReceive: hasFinancingCommissionToReceive.value,
  isSeparateInvoices: isSeparateInvoices.value,
  isSingleInvoice: isSingleInvoice.value,
  hasCorrectionLetter: stepStatus.value === PaymentModelStatus.TO_REVIEW,
}))

const visibleSections = computed<ReceiptModelSectionKey[]>(() => {
  const sections: Array<[ReceiptModelSectionKey, boolean]> = [
    ['model', payoutOptions.value.length > 0],
    ['supplier', showSupplierSection.value],
    ['equipment', isContainer.value || isComboStoreOrder.value],
    ['invoices', invoicesToShow.value],
  ]
  return sections.filter(([, isVisible]) => isVisible).map(([section]) => section)
})

function hasDividerBefore(section: ReceiptModelSectionKey): boolean {
  return visibleSections.value.indexOf(section) > 0
}

const selectedSupplierId = ref<number | null>(null)
const isSelectedSupplierPartner = computed(() =>
  resolveSupplierIsPartner(selectedSupplierId.value, suppliersData.value, paymentModelData.value?.supplier),
)

const supplierForm: SupplierSchema.Form = useForm({
  initialValues: SupplierSchema.getInitialValues(),
  validationSchema: computed(() => toTypedSchema(SupplierSchema.getSchema(t, { isPartner: isSelectedSupplierPartner.value }))),
  validateOnMount: false,
})

watch(() => supplierForm.values.hardware_supplier, (supplier) => {
  selectedSupplierId.value = getSelectedSupplierId(supplier)
}, { immediate: true })

watch(
  [() => paymentModelData.value?.supplier?.id, suppliersData],
  ([savedSupplierId, suppliers]) => {
    if (savedSupplierId != null && suppliers?.length)
      selectedSupplierId.value = savedSupplierId
  },
  { immediate: true },
)

const invoiceForm: InvoiceSchema.Form = useForm({
  initialValues: InvoiceSchema.getInitialValues(),
  validationSchema: computed(() => toTypedSchema(InvoiceSchema.getSchema({
    t,
    requireServiceProductInvoice: isSingleInvoice.value || isContainer.value,
    isSeparateInvoices: isSeparateInvoices.value,
    hasFinancingCommissionToReceive: hasFinancingCommissionToReceive.value,
    isPartnerSupplier: isSelectedSupplierPartner.value,
    paymentModelStatus: paymentModelData.value?.status,
  }))),
  validateOnMount: false,
})

const kitValue = computed(() => (supplierForm.values.equipment_value || paymentModelData.value?.equipment_value) ?? 0)
const selectedSupplierName = computed(() => getSelectedSupplierName(supplierForm.values.hardware_supplier))
const isEquipmentInvoiceDisabled = computed(() =>
  showSupplierSection.value && (!selectedSupplierName.value || !kitValue.value),
)
const isReimbursableSupplier = computed(() =>
  isNonComboFlow.value && showSupplierSection.value && !isSelectedSupplierPartner.value,
)
const receivablesSupplierName = computed(() => {
  if (!isNonComboFlow.value)
    return t('project_data.equipment_summary.distributor_name')

  return selectedSupplierName.value || formatSupplierName(paymentModelData.value?.supplier?.name) || null
})
const receivablesProjectId = computed(() => financing.value?.project_id ?? null)

async function validateAllForms(): Promise<boolean> {
  const validations: Array<Promise<{ valid: boolean }>> = [invoiceForm.validate()]
  if (showSupplierSection.value)
    validations.push(supplierForm.validate())

  const results = await Promise.all(validations)
  return results.every(result => result.valid)
}

const alertError = ref<PaymentModelAlert>({ title: '', description: '', type: 'error' })
const alertSuccess = ref<PaymentModelAlert>({ title: '', description: '', type: 'success' })

function applyStatusAlerts(status: string | null | undefined) {
  if (status === PaymentModelStatus.APPROVED) {
    alertSuccess.value = {
      title: t('receipt-model.layout.approved_alert.title'),
      description: t('receipt-model.layout.approved_alert.text'),
      type: 'success',
    }
  }

  if (status === PaymentModelStatus.TO_REVIEW) {
    alertError.value = {
      title: t('receipt-model.layout.invoice_divergent_alert.title'),
      description: t('receipt-model.layout.invoice_divergent_alert.text'),
      type: 'warning',
    }
  }
}

watch(paymentModelData, (data) => {
  if (data)
    applyStatusAlerts(data.status)
}, { immediate: true })

watch(() => props.statusProject, async () => {
  await access.refreshAccess()
  applyStatusAlerts(stepStatus.value)
}, { immediate: true })

const hasInvoiceDenied = computed(() =>
  paymentModelData.value?.invoice?.some(item =>
    item.invoice_status === InvoiceStatus.DENIED && item.invoice_kind !== InvoiceKind.CORRECTION_LETTER,
  ) ?? false,
)

const deadlineAlertTitle = computed(() => {
  const date = paymentModelData.value?.expired_date ?? ''
  return date
    ? t('customer.invoice_deadline_alert.title', { date })
    : t('customer.invoice_deadline_alert.title_no_date')
})

const showDeadlineAlert = computed(() => {
  const statuses = [
    stepStatus.value,
    financing.value?.section_statuses?.payment_model?.status,
    paymentModelData.value?.status,
  ]
  if (statuses.some(status => status && DEADLINE_ALERT_HIDDEN_STATUSES.has(status)))
    return false

  return invoicesToShow.value && !hasShownStatus.value
})

const supplierInformationRef = ref<InstanceType<typeof SupplierInformationForm> | null>(null)
const supplierSectionRef = ref<HTMLElement | null>(null)
const isConfirmationOpen = ref(false)
const isSubmitting = ref(false)
const showInvoiceErrorsAfterSubmit = ref(false)

function notifyIncompleteDocumentation() {
  createErrorToast({
    title: t('toast.incomplete.documentation.title'),
    description: t('toast.incomplete.documentation.description'),
    actionText: '',
  })
}

function notifyMissingPayoutOption() {
  createErrorToast({
    title: t('receipt-model.layout.missing_payout_option'),
    description: '',
  })
}

// Terminal financing: the polling (15 s) may turn the status terminal while the modal is open.
watch(isTerminalReadOnly, (isReadOnly) => {
  if (isReadOnly)
    isConfirmationOpen.value = false
})

async function handleSendClick() {
  if (isTerminalReadOnly.value)
    return

  showInvoiceErrorsAfterSubmit.value = true

  if (!(await validateAllForms())) {
    notifyIncompleteDocumentation()
    return
  }

  if (!selectedPayoutOption.value) {
    notifyMissingPayoutOption()
    return
  }

  isConfirmationOpen.value = true
}

function closeConfirmation() {
  isConfirmationOpen.value = false
}

async function postReceiptModel(body: PostPaymentModelBody) {
  if (isTerminalReadOnly.value) {
    isSubmitting.value = false
    return
  }

  try {
    await mutateUpdatePaymentModel(body)
    createSuccessToast({
      title: t('receiptModel.response.success.title'),
      description: t('receiptModel.response.success.description'),
    })

    if (hasCombo.value)
      hotjar.track('receipt_model_sent_with_combo', 'Pesquisa disparada após envio do modelo de recebimento para cliente com combo fácil')

    const queryKey = queryKeys.PAYMENT_MODEL.DATA(projectId.value)
    await queryClient.invalidateQueries({ queryKey })
    await queryClient.refetchQueries({ queryKey })
    showInvoiceErrorsAfterSubmit.value = false
  }
  catch (error: unknown) {
    console.error(error)

    if (receiptModelFlow.getReceiptModelErrorReasonCode(error) === STORE_ORDER_EXPIRED_REASON_CODE) {
      createErrorToast({
        title: t('receiptModel.response.store_order_expired'),
        description: '',
      })
    }
    else {
      createErrorToast({
        title: t('receiptModel.response.error.title'),
        description: t('receiptModel.response.error.description'),
      })
    }
  }
  finally {
    isSubmitting.value = false
  }
}

async function sendFiles() {
  if (isTerminalReadOnly.value) {
    isConfirmationOpen.value = false
    return
  }

  if (!paymentModelData.value)
    return

  isConfirmationOpen.value = false
  showInvoiceErrorsAfterSubmit.value = true

  if (!(await validateAllForms())) {
    notifyIncompleteDocumentation()
    return
  }

  const payoutOption = selectedPayoutOption.value
  if (!payoutOption) {
    notifyMissingPayoutOption()
    return
  }

  hotjar.track('clicked_btn_enviar_MR', 'Clique no botão Enviar em Modelo de Recebimento no modal de confirmação')

  // The letter is resent with the current e-mails; a failure shows its own toast/field error and the POST goes on.
  if (showSupplierSection.value && isSelectedSupplierPartner.value) {
    try {
      await supplierInformationRef.value?.sendEmail(false)
    }
    catch (error) {
      console.error('Error sending email:', error)
    }
  }

  isSubmitting.value = true

  await postReceiptModel(receiptModelFlow.buildPostPaymentModelBody({
    projectId: projectId.value,
    payoutOption,
    // `0` when no distributor was selected (flows without supplier): the backend treats it as no supplier.
    supplierId: supplierForm.values.hardware_supplier?.value ?? null,
    equipmentValue: receiptModelFlow.getEquipmentValueToSend({
      hasCombo: hasCombo.value,
      equipmentOrigin: equipmentOrigin.value,
      receiptModel: receiptModel.value,
      kitValue: kitValue.value,
    }),
    equipmentOrigin: equipmentOrigin.value,
  }))

  supplierSectionRef.value?.scrollIntoView({ behavior: 'smooth' })
  access.refetchFinancing()
}
</script>

<template>
  <CustomerElementAccordion
    :id="`accordion_${Formalization.PAYMENT_MODEL}`"
    :title="t('customer.receipt_model_title')"
    :position="String(position)"
    :tag="tag"
    :blocked="isBlocked"
    :open-collapse="openCollapse"
    :has-shown-status="hasShownStatus"
    v-bind="showDateProps ? dateProps : null"
    class="-mt-6"
  >
    <template #icon>
      <SolTag v-if="showPendingEquipmentTag" id="pending-update-equipment-tag" size="small">
        {{ t('customer.equipment_update.tag.mr_pending') }}
      </SolTag>
      <IconLock v-if="isBlocked" />
    </template>

    <template v-if="isLoadingPaymentModel">
      <div class="container">
        <div class="my-4 card-container flex flex-col justify-between w-full">
          <span class="loader-project" />
        </div>
      </div>
    </template>

    <PaymentModelLoadError
      v-else-if="errorPaymentModel"
      :error="errorPaymentModel"
      @retry="refetchPaymentModel()"
    />

    <template v-else-if="paymentModelData">
      <div class="step-receipt">
        <div class="receipt-model">
          <PaymentModelHeader
            :has-combo-or-mirror="hasComboOrMirror"
            :show-deadline-alert="showDeadlineAlert"
            :deadline-alert-title="deadlineAlertTitle"
            :step-status="stepStatus"
            :invoices-to-show="invoicesToShow"
            :has-invoice-denied="hasInvoiceDenied"
            :uses-invoice-denied-alert="isNonComboFlow || isContainer"
            :alert-error="alertError"
            :alert-success="alertSuccess"
          />

          <div class="receipt-model__columns">
            <div class="receipt-model__main">
              <ReceiptModelSection
                v-if="payoutOptions.length"
                :options="payoutOptions"
                :model-value="selectedPayoutOption"
                :disabled="isFieldLocked"
                :readonly="hasComboOrMirror"
                @update:model-value="handlePayoutOptionSelect"
              />

              <!-- v-show, not v-if: the form stays mounted so its watchers fill it from the GET -->
              <div v-if="hasDividerBefore('supplier')" class="receipt-model__divider" aria-hidden="true" />
              <div v-show="showSupplierSection" :id="SUPPLIER_SECTION_ID" ref="supplierSectionRef">
                <SupplierInformationForm
                  ref="supplierInformationRef"
                  :form="supplierForm"
                  :payment-model="paymentModelData"
                  :disable-field="isFieldLocked"
                  :read-only="isTerminalReadOnly"
                  :project-id="projectId"
                />
              </div>

              <div v-if="hasDividerBefore('equipment')" class="receipt-model__divider" aria-hidden="true" />
              <EquipmentSummary v-if="isContainer" variant="container" />
              <EquipmentSummary v-else-if="isComboStoreOrder" variant="combo" />

              <!-- v-show, not v-if: same reason as the supplier form -->
              <div v-if="hasDividerBefore('invoices')" class="receipt-model__divider" aria-hidden="true" />
              <div v-show="invoicesToShow">
                <InvoicesForm
                  :form="invoiceForm"
                  :data="paymentModelData"
                  :project-id="projectId"
                  :options="invoiceFormOptions"
                  :disable-field="isFieldLocked || isSubmitting"
                  :read-only="isTerminalReadOnly"
                  :step-status="stepStatus"
                  :equipment-origin="equipmentOrigin"
                  :is-combo-store-order="isComboStoreOrder"
                  :show-errors-after-submit="showInvoiceErrorsAfterSubmit"
                  :equipment-invoice-disabled="isEquipmentInvoiceDisabled"
                />

                <CommissionInvoiceInfo
                  v-if="hasFinancingCommissionToReceive"
                  :solfacil-data="paymentModelData.solfacil_info"
                  :commission-value="paymentModelData.commissions?.total?.value"
                />
              </div>
            </div>

            <div class="receipt-model__aside">
              <ReceivableValues
                :data="paymentModelData"
                :step-status="stepStatus"
                :supplier-name="receivablesSupplierName"
                :is-reimbursable="isReimbursableSupplier"
                :project-id="receivablesProjectId"
                :expects-invoices="invoicesToShow"
              />
            </div>
          </div>
        </div>

        <PaymentModelFooter
          :show-dates="showDateProps"
          :submitted-at="submittedAt"
          :completed-at="completedAt"
          :reproved-at="reprovedAt"
          :loading="isSubmitting"
          :disabled="isSendDisabled"
          :is-combo-store-order="isComboStoreOrder"
          :step-status="stepStatus"
          @submit="handleSendClick()"
        />
      </div>
    </template>
  </CustomerElementAccordion>

  <ConfirmationModal
    v-if="isConfirmationOpen"
    :is-vkit="paymentModelData?.is_vkit ?? false"
    @send="sendFiles()"
    @close="closeConfirmation()"
  />
</template>

<style lang="scss">
.step-receipt {
  .receipt-model {
    @apply flex flex-col gap-6;
  }

  .receipt-model__columns {
    @apply flex flex-col gap-8;

    @screen md:system {
      @apply flex-row items-start;
    }
  }

  .receipt-model__main {
    @apply flex flex-col gap-8 w-full min-w-0;

    @screen md:system {
      flex: 2 1 0%;
    }
  }

  .receipt-model__aside {
    @apply w-full min-w-0;

    @screen md:system {
      @apply sticky top-10;

      flex: 1 1 0%;
    }
  }

  // The accordion applies `-mx-4` to every `div p` below lg:system; the panel's SolEmptyState subtitle is a <p>.
  .receipt-model__aside .sol-empty-state-core .subtitle {
    @apply mx-0;
  }

  // Fixed 20px: `fonts-heading-h3` grows to 24px from 1024px and would match the accordion title.
  .receipt-model__section-title {
    @apply font-highlight font-regular text-xs leading-xs text-neutral-low-dark;
  }

  .receipt-model__divider {
    @apply w-full h-px bg-neutral-high-medium;
  }

  .receipt-model__inline-link {
    @apply inline p-0 font-bold underline cursor-pointer;

    color: inherit;
    font-size: inherit;
    line-height: inherit;

    &:focus-visible {
      @apply outline-focused;
    }
  }

  .sol-button-core.-secondary {
    min-width: fit-content !important;
  }

  .sol-alert-core.-informative {
    @apply flex flex-col md:site:flex-row gap-4 md:site:gap-0;
  }
}

.sol-upload {
  @apply p-5;
}

.sol-select-core>.select-button-toggle {
  @apply h-auto;
}

.sol-input-currency-core>.container-input {
  @apply ml-1;
}
</style>
